import { useId, useState } from 'react'
import type { Game } from '@/catalog/types'
import { cn } from '@/lib/cn'
import { hashString } from '@/lib/format'

/** Accent/base color pairs for generated cover art. */
const PALETTES: Array<[string, string]> = [
  ['#c6ff4a', '#1d5e45'],
  ['#ff5f7e', '#4b2380'],
  ['#5ad1ff', '#26307e'],
  ['#ffc24a', '#a3332f'],
  ['#b69cff', '#1f2a66'],
  ['#4affc4', '#11497a'],
]

const PATTERNS = ['dots', 'grid', 'rings', 'stripes'] as const

function CoverPattern({ kind, id }: { kind: (typeof PATTERNS)[number]; id: string }) {
  const shapes = {
    dots: <circle cx="6" cy="6" r="1.4" fill="white" />,
    grid: <path d="M12 0H0v12" fill="none" stroke="white" strokeWidth="0.8" />,
    rings: <circle cx="12" cy="12" r="8" fill="none" stroke="white" strokeWidth="0.9" />,
    stripes: <path d="M-3 3l6-6M0 12 12 0M9 15l6-6" stroke="white" strokeWidth="1.4" />,
  }
  const size = kind === 'rings' ? 24 : 12
  return (
    <svg className="absolute inset-0 size-full" aria-hidden="true">
      <defs>
        <pattern id={id} width={size} height={size} patternUnits="userSpaceOnUse">
          {shapes[kind]}
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} />
    </svg>
  )
}

/** Deterministic artwork derived from the game id, used when no thumbnail exists. */
function GeneratedCover({ game, large }: { game: Game; large: boolean }) {
  const patternId = useId()
  const hash = hashString(game.id)
  const [accent, base] = PALETTES[hash % PALETTES.length]!
  const pattern = PATTERNS[(hash >>> 4) % PATTERNS.length]!
  const x = 20 + ((hash >>> 8) % 60)
  const y = 15 + ((hash >>> 14) % 50)
  const initial = game.title.replace(/^the\s+/i, '').charAt(0).toUpperCase()

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      style={{
        backgroundColor: base,
        backgroundImage: [
          `radial-gradient(circle at ${x}% ${y}%, ${accent} 0%, transparent 55%)`,
          `radial-gradient(circle at ${100 - x}% 110%, color-mix(in oklab, ${accent} 45%, transparent) 0%, transparent 60%)`,
          'linear-gradient(160deg, transparent 40%, rgb(0 0 0 / 0.45))',
        ].join(','),
      }}
    >
      <div className="absolute inset-0 opacity-[0.13] [mask-image:linear-gradient(180deg,black,transparent_85%)]">
        <CoverPattern kind={pattern} id={patternId} />
      </div>
      <span
        aria-hidden="true"
        className={cn(
          'absolute right-[6%] bottom-[-0.18em] font-display leading-none font-extrabold text-white/90 mix-blend-overlay select-none',
          large ? 'text-[min(22rem,48vw)]' : 'text-[9rem]',
        )}
      >
        {initial}
      </span>
      <div className="grain absolute inset-0" />
    </div>
  )
}

interface GameCoverProps {
  game: Game
  /** Must position and size the cover (e.g. `absolute inset-0`). */
  className?: string
  /** Larger type treatment for hero/feature placements. */
  large?: boolean
}

export function GameCover({ game, className, large = false }: GameCoverProps) {
  const [failed, setFailed] = useState(false)
  const showImage = game.thumbnail && !failed

  return (
    <div className={cn('overflow-hidden bg-ink-800', className)}>
      {showImage ? (
        <img
          src={game.thumbnail!}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="absolute inset-0 size-full object-cover"
        />
      ) : (
        <GeneratedCover game={game} large={large} />
      )}
    </div>
  )
}
